import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useToast } from '@/components/ui/Toast';
import { FolderPicker } from './FolderPicker';

/**
 * Memindahkan satu berkas atau folder ke folder lain.
 *
 * `item` berbentuk { type: 'file' | 'folder', item }. Untuk folder, pilihan
 * tujuannya otomatis mengecualikan dirinya dan keturunannya - server juga
 * menolaknya, tapi lebih baik pilihannya tidak pernah muncul.
 */
export const MoveDialog = ({ target, onClose }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [folderId, setFolderId] = useState('');

  const pindah = useMutation({
    mutationFn: () =>
      api.post('/storage/move', {
        fileIds: target.type === 'file' ? [target.item.id] : [],
        folderIds: target.type === 'folder' ? [target.item.id] : [],
        targetFolderId: folderId || null,
      }),
    onSuccess: () => {
      toast.success('Dipindahkan');
      queryClient.invalidateQueries({ queryKey: ['storage'] });
      queryClient.invalidateQueries({ queryKey: ['storage-folders-all'] });
      onClose();
    },
    onError: (err) =>
      toast.error('Gagal memindahkan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  if (!target) return null;
  const nama = target.type === 'file' ? target.item.filename : target.item.name;

  return (
    <Modal
      open
      onClose={onClose}
      title="Pindahkan"
      description={`Pindahkan "${nama}" ke folder lain.`}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Batal
          </Button>
          <Button loading={pindah.isPending} onClick={() => pindah.mutate()}>
            Pindahkan
          </Button>
        </div>
      }
    >
      <Field label="Folder tujuan">
        {(props) => (
          <FolderPicker
            {...props}
            value={folderId}
            onChange={setFolderId}
            excludeId={target.type === 'folder' ? target.item.id : undefined}
          />
        )}
      </Field>
    </Modal>
  );
};
